import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import throttle from 'lodash/throttle';
import Head from 'next/head';
import { useIsomorphicLayoutEffect } from '../../../adapters/helpers/Hooks';
import { getBackgroundImage, roundUpTheSize } from './BackgroundImage.helpers';
import { mediaQueryHOC } from '../../../adapters/helpers/Hooks';
import { ImageConstants } from '../../../adapters/helpers/Constants';
import { optimizeImage } from '../../../adapters/helpers/pagespeedHelper';
import ImageFactory from '../../../adapters/cloudinary/ImageFactory';
import LazyLoad from 'react-lazy-load';
function BackgroundImage(props) {
  var _props$mobileBackgrou, _props$mobileBackgrou2, _props$mobileBackgrou3, _props$mobileBackgrou4, _props$desktopBackgro, _props$desktopBackgro2, _props$desktopBackgro3, _props$desktopBackgro4;
  const mobileBackgroundImageId = (props == null ? void 0 : (_props$mobileBackgrou = props.mobileBackgroundImage) == null ? void 0 : (_props$mobileBackgrou2 = _props$mobileBackgrou.asset) == null ? void 0 : (_props$mobileBackgrou3 = _props$mobileBackgrou2.fields) == null ? void 0 : (_props$mobileBackgrou4 = _props$mobileBackgrou3.file) == null ? void 0 : _props$mobileBackgrou4.url) || props.contentImageId;
  const desktopBackgroundImageId = (props == null ? void 0 : (_props$desktopBackgro = props.desktopBackgroundImage) == null ? void 0 : (_props$desktopBackgro2 = _props$desktopBackgro.asset) == null ? void 0 : (_props$desktopBackgro3 = _props$desktopBackgro2.fields) == null ? void 0 : (_props$desktopBackgro4 = _props$desktopBackgro3.file) == null ? void 0 : _props$desktopBackgro4.url) || props.contentImageId;
  const {
    backgroundOffsetTop: marginTop,
    backgroundPosition,
    alt = null,
    children = null,
    customStyles = null,
    ariaHidden = false
  } = props;
  const paddingTop = `${100 / props.widthToHeightRatio}%`;
  const isPreload = props.isPreload ? props.isPreload : false;
  const isBannerImage = props.isBannerImage ? props.isBannerImage : false;
  const nodeRef = useRef(null);
  const placeholderRef = useRef(null);
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  const [width, setWidth] = useState(ImageConstants.defaultImageWindWidth);
  const [customClassName, setCustomClassName] = useState('');
  const [url, setUrl] = useState(`url(${optimizeImage(ImageFactory.buildContentfullImageUrl(props.contentImageId))})`);
  function updateWidth() {
    const node = nodeRef.current || placeholderRef.current;
    if (node) {
      setWidth(roundUpTheSize(node.getBoundingClientRect().width));
    }
  }
  useEffect(() => {
    setIsPageLoaded(true);
    if (width != 0) {
      // Set background width*2 for mobile because the background is larger than the with parent
      setUrl(getBackgroundImage(props.contentImageId, isPreload && isBannerImage && window.innerWidth < 400 ? 450 : props.isMedium ? width * 2 : width, props.transformations, props.format, props.isMedium));
    }
  }, [width, props.contentImageId, props.isMedium, isPageLoaded]);
  useEffect(() => {
    const handleResize = throttle(updateWidth, 100);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      handleResize.cancel();
    };
  }, []);
  useEffect(() => {
    setCustomClassName(props.customClassName);
  }, [props.customClassName]);
  useIsomorphicLayoutEffect(updateWidth, []);
  return <React.Fragment>
            {isPreload && isBannerImage ? <React.Fragment>
                    <Head>
                        {/* Preload above-the-fold background images with high priority and srcset for responsive loading */}
                        <link rel="preload" href={optimizeImage(ImageFactory.buildContentfullImageUrl(mobileBackgroundImageId))} as="image" media={`(max-width:${ImageConstants.mobile_max_width})`} fetchpriority='high' imagesrcset={width > 0 ? ImageFactory.buildContentfullImageSrcSet(mobileBackgroundImageId, width, props.transformations, props.format) : undefined} imagesizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw" />
                        <link rel="preload" href={optimizeImage(ImageFactory.buildContentfullImageUrl(desktopBackgroundImageId))} as="image" media={`(min-width:${ImageConstants.desktop_min_width})`} fetchpriority='high' imagesrcset={width > 0 ? ImageFactory.buildContentfullImageSrcSet(desktopBackgroundImageId, width, props.transformations, props.format) : undefined} imagesizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw" />
                    </Head>
                    {/* Below code structure have implemented for pagespeed. */}
                    {isPageLoaded ? <div className={`ob-background-wrapper ${width === 0 ? 'ob-background-wrapper-height' : ''} ${props.className ? props.className : ''} ${customClassName ? customClassName : ''}`} ref={nodeRef} aria-hidden={ariaHidden} data-fetchpriority='high' style={{
        marginTop,
        paddingTop,
        backgroundPosition,
        backgroundImage: `url(${optimizeImage(ImageFactory.buildContentfullImageUrl(props.contentImageId))})`,
        ...customStyles
      }}>
                            <img src={optimizeImage(ImageFactory.buildContentfullImageUrl(props.contentImageId))} alt={alt || ''} fetchPriority="high" style={{
          display: 'none'
        }} />
                            {alt && <span role="img" aria-label={alt}></span>}
                            {children}
                        </div> : <React.Fragment>
                            <div className={`ob-background-wrapper ob-background-wrapper-mobile ${props.className ? props.className : ''} ${customClassName ? customClassName : ''}`} ref={nodeRef} aria-hidden={ariaHidden} data-fetchPriority="high" style={{
          marginTop,
          paddingTop,
          backgroundPosition,
          backgroundImage: `url(${optimizeImage(ImageFactory.buildContentfullImageUrl(mobileBackgroundImageId))})`,
          ...customStyles
        }}>
                                <img src={optimizeImage(ImageFactory.buildContentfullImageUrl(mobileBackgroundImageId))} alt={alt || ''} fetchPriority="high" style={{
            display: 'none'
          }} />
                                {alt && <span role="img" aria-label={alt} aria-hidden='true'></span>}
                                {children}
                            </div>
                            <div className={`ob-background-wrapper ob-background-wrapper-desktop ${width === 0 ? 'ob-background-wrapper-height' : ''} ${props.className ? props.className : ''} ${customClassName ? customClassName : ''}`} ref={nodeRef} aria-hidden={ariaHidden} data-fetchPriority="high" style={{
          marginTop,
          paddingTop,
          backgroundPosition,
          backgroundImage: `url(${optimizeImage(ImageFactory.buildContentfullImageUrl(desktopBackgroundImageId))})`,
          ...customStyles
        }}>
                                <img src={optimizeImage(ImageFactory.buildContentfullImageUrl(desktopBackgroundImageId))} alt={alt || ''} fetchPriority="high" style={{
            display: 'none'
          }} />
                                {alt && <span role="img" aria-label={alt}></span>}
                                {children}
                            </div>
                        </React.Fragment>}
                </React.Fragment> : <>
                    <LazyLoad offset={500} className='test' placeholder={<div className={`ob-background-wrapper ${props.className ? props.className : ''} ${customClassName}`} ref={placeholderRef} aria-hidden={ariaHidden} style={{
        marginTop,
        paddingTop,
        backgroundPosition
      }} />}>
                        <div className={`ob-background-wrapper ${props.className ? props.className : ''} ${customClassName ? customClassName : ''}`} ref={nodeRef} aria-hidden={ariaHidden} style={{
          marginTop,
          paddingTop,
          backgroundPosition,
          backgroundImage: !props.isLarge && !props.isMedium || props.isLarge && !props.isMedium ? `url(${optimizeImage(ImageFactory.buildContentfullImageUrl(desktopBackgroundImageId))})` : `url(${optimizeImage(ImageFactory.buildContentfullImageUrl(mobileBackgroundImageId))})`,
          ...customStyles
        }}>
                        <img src={!props.isLarge && !props.isMedium || props.isLarge && !props.isMedium ? optimizeImage(ImageFactory.buildContentfullImageUrl(desktopBackgroundImageId)) : optimizeImage(ImageFactory.buildContentfullImageUrl(mobileBackgroundImageId))} alt={alt || ''} fetchPriority="high" style={{
            display: 'none'
          }} />
                            {alt && <span role="img" aria-label={alt} aria-hidden='true'></span>}
                            {children}
                        </div>
                    </LazyLoad>
                    </>}
        </React.Fragment>;
}
export default mediaQueryHOC(BackgroundImage);
BackgroundImage.defaultProps = {
  transformations: 'c_limit',
  fragments: [{
    minWidth: 0,
    size: 1
  }],
  widthToHeightRatio: 1
};
BackgroundImage.propTypes = {
  imageId: PropTypes.string,
  transformations: PropTypes.string,
  className: PropTypes.string,
  customClassName: PropTypes.string,
  fragments: PropTypes.array,
  widthToHeightRatio: PropTypes.number,
  children: PropTypes.node,
  backgroundOffsetTop: PropTypes.string,
  backgroundPosition: PropTypes.string,
  customStyles: PropTypes.object,
  format: PropTypes.any,
  isMedium: PropTypes.bool,
  ariaHidden: PropTypes.bool,
  alt: PropTypes.string,
  isPreload: PropTypes.bool,
  isBannerImage: PropTypes.bool,
  preloadImage: PropTypes.any,
  desktopBackgroundImage: PropTypes.any,
  mobileBackgroundImage: PropTypes.any
};